import React, { useEffect, useMemo, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import "./Navbar.css";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const location = useLocation();

  const PHONE_DISPLAY = "(770) 876-7485";
  const PHONE_TEL = "+17708767485";
  const ADDRESS_DISPLAY = "5511 112th Ave E, Suite B, Puyallup, WA 98372";
  const ADDRESS_MAPS = useMemo(
    () =>
      "https://www.google.com/maps/search/?api=1&query=5511+112th+Ave+E,+Suite+B,+Puyallup,+WA+98372",
    []
  );

  const closeMenu = () => setOpen(false);
  const toggleMenu = () => setOpen((v) => !v);

  // Close menu on route change (important for mobile)
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Escape + resize close + scroll shadow
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };

    const onResize = () => {
      if (window.innerWidth > 980) setOpen(false);
    };

    const onScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, { passive: true });

    // init
    onScroll();

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  // Lock body scroll while drawer is open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header className={`site-header ${scrolled ? "is-scrolled" : ""}`}>
      {/* Top info bar */}
      <div className="topbar" role="contentinfo">
    
      </div>

      {/* Main nav bar */}
      <div className="navbar" role="navigation" aria-label="Main">
        <div className="container navbar__row">
          <Link to="/" className="brand" aria-label="Homepage">
            <img src="/logoo.png" alt="A Peaceful Retreat" className="brand__logo" />
          </Link>

          {/* Desktop menu */}
          <nav className="menu" aria-label="Primary">
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                `menu__link ${isActive ? "is-active" : ""}`
              }
            >
              Home
            </NavLink>

            <NavLink
              to="/about"
              className={({ isActive }) =>
                `menu__link ${isActive ? "is-active" : ""}`
              }
            >
              About
            </NavLink>

            <NavLink
              to="/services"
              className={({ isActive }) =>
                `menu__link ${isActive ? "is-active" : ""}`
              }
            >
              Services
            </NavLink>

            <a
              href="https://www.vagaro.com/apeacefulretreat"
              className="menu__link"
              target="_blank"
              rel="noreferrer"
            >
              Contact
            </a>
          </nav>

          {/* Desktop call */}
          <a className="call" href={`tel:${PHONE_TEL}`} aria-label="Call now">
            <span className="call__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="16" height="16">
                <path
                  d="M6.6 10.8a15.1 15.1 0 006.6 6.6l2.2-2.2a1.3 1.3 0 011.3-.3c1.4.5 2.9.8 4.5.8a1.3 1.3 0 011.3 1.3V20a2 2 0 01-2 2C9.8 22 2 14.2 2 4a2 2 0 012-2h3c.7 0 1.3.5 1.3 1.3 0 1.6.3 3.1.8 4.5.2.5.1 1-.3 1.3l-2.2 2.2z"
                  fill="currentColor"
                />
              </svg>
            </span>
            {PHONE_DISPLAY}
          </a>

          {/* Mobile hamburger */}
          <button
            type="button"
            className={`burger ${open ? "is-active" : ""}`}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            aria-controls="mobile-menu"
            onClick={toggleMenu}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </div>

      {/* Mobile drawer (rendered always, but accessible only when open) */}
      <div
        id="mobile-menu"
        className={`mobile ${open ? "mobile--open" : ""}`}
        role="dialog"
        aria-modal={open ? "true" : "false"}
        aria-hidden={open ? "false" : "true"}
        onClick={closeMenu}
      >
        <div className="mobile__panel" onClick={(e) => e.stopPropagation()}>
          <div className="mobile__head">
            <Link to="/" className="brand brand--mobile" onClick={closeMenu}>
              <img src="/logoo.png" alt="A Peaceful Retreat" className="brand__logo" />
            </Link>

            <button
              type="button"
              className="close"
              aria-label="Close menu"
              onClick={closeMenu}
            >
              ×
            </button>
          </div>

          <nav className="mobile__nav" aria-label="Mobile">
            <NavLink
              to="/"
              end
              className={({ isActive }) => (isActive ? "is-active" : "")}
              onClick={closeMenu}
            >
              Home
            </NavLink>

            <NavLink
              to="/about"
              className={({ isActive }) => (isActive ? "is-active" : "")}
              onClick={closeMenu}
            >
              About
            </NavLink>

            <NavLink
              to="/services"
              className={({ isActive }) => (isActive ? "is-active" : "")}
              onClick={closeMenu}
            >
              Services
            </NavLink>

            <a
              href="https://www.vagaro.com/apeacefulretreat"
              target="_blank"
              rel="noreferrer"
              onClick={closeMenu}
            >
              Contact
            </a>
          </nav>

          <div className="mobile__contact">
            <a href={`tel:${PHONE_TEL}`} className="mobile__line">
              <strong>Phone:</strong> {PHONE_DISPLAY}
            </a>
            <a
              href={ADDRESS_MAPS}
              target="_blank"
              rel="noreferrer"
              className="mobile__line"
            >
              <strong>Address:</strong> {ADDRESS_DISPLAY}
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}